"use client";

/**
 * The CFG Builder surface: a form, a dropped `resources/` folder, and an
 * existing `server.cfg` to merge into.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE IS AND IS NOT
 *
 * It is a wiring harness. Every decision worth arguing about — what a value is
 * quoted as, what a clamp is called, which resource starts before which, which
 * of the eleven `cfg.*` rules fires — lives in `@/lib/gtaKnowledge/serverCfg`
 * and is tested there. Nothing in this file sorts, quotes, escapes or judges.
 *
 * That boundary is not tidiness. The competitor's generator is sold on load
 * order and contains no ordering logic, and the reason a tool can ship in that
 * state is that ordering is invisible from the surface: a list of `ensure` lines
 * looks equally plausible in any sequence. So the surface must never be in a
 * position to produce one. It asks `orderCfgResources` for an order and prints
 * what it is handed, including the sentence that says whether the order is real
 * — `ensureLines` in the library writes `NOT ORDERED` at the top of a block it
 * could not sort, and this page has no way to suppress that.
 *
 * THE ONE ORDERING RULE THIS FILE HAS TO GET RIGHT
 *
 * `mergeServerCfg` has no graph parameter: it writes the order it is handed. So
 * `orderCfgResources(resources, graph).resources` is computed once here, in
 * `useMemo`, and the same array is handed to the generator, to the merge and to
 * the analyzer. Calling merge with the folder's raw discovery order would write
 * a file whose ensure block is in directory order while the panel above it
 * reports the ordered one — the two halves of the screen disagreeing about the
 * only thing the tool is for.
 *
 * WHY THE SHELL IS RENDERED HERE AND NOT BY THE PAGE
 *
 * The island owns the shell and the server page passes its header and its body
 * copy through as nodes: the prose is still rendered on the server and still in
 * the prerendered HTML for search, and the width is still one decision made in
 * `FreeToolShell`.
 *
 * WHY THE WIDTH IS NO LONGER CONDITIONAL
 *
 * It used to be `prose` while the tool was an empty form and `wide` once a
 * folder was loaded, on the argument that a folder of two hundred resources is
 * a long list of paths that a 48rem reading column wraps to three lines each.
 * Two things were wrong with that. Dropping a folder reflowed every field the
 * user was working in, mid-edit, because they attached a folder — a layout
 * change nobody asked for. And it made the output rail impossible: a sticky
 * column cannot exist in a 768px measure and then appear later, and the rail is
 * what fixes the real defect, which is that the panel explaining why the file
 * says 2048 when the box says 999999 used to render two thousand pixels below
 * the box. `FreeToolShell`'s reading-measure argument is not discarded — it is
 * about prose, and the page's `<article>` keeps its own reading column. The
 * invariant that shell actually defends, "chrome is never narrower than
 * content", is untouched: `wide` sets both to `max-w-7xl`.
 *
 * THE HONESTY RULE, AS IT APPLIES TO A GENERATOR
 *
 * A tool that reads is dishonest when it reports a clean result over input it
 * never read. A tool that *writes* has a second way to be dishonest: writing a
 * file that does not say what it did to the user's values. Both are covered
 * from one place — `normalizeCfgConfig` returns its adjustments, the generated
 * file repeats them as comments, and this page renders them above the output.
 * The input box keeps showing 999999 and the file says 2048 and the panel says
 * why, which is the whole of the fix for the silent clamp.
 *
 * There is a third way, and it belongs to import mode alone: writing a value the
 * user never typed into a file they already run. A form always holds something —
 * `DEFAULT_SERVER_CFG_FORM` when nobody has typed — and `mergeServerCfg` writes
 * every field its configuration has an opinion about, so handing it a fully
 * populated form rewrote a pasted config's hostname, port, slot count and
 * hardening flags with strangers' values under a line reading "everything else
 * is byte for byte what you gave us". Two things fix it and both are here:
 * `formFromCfg` fills the form in from the pasted config before anything is
 * merged, so the values written back are the user's own; and the merge is
 * rendered as a list of lines — what your file said, what this patch writes —
 * rather than as a count, because a count is a reassurance and cannot be checked.
 *
 * LIMITS
 *
 *   - Nothing here validates a licence key, a connection string or an admin
 *     identifier against anything. They are strings written into a file.
 *   - The merge is a static text patch. It cannot read an `exec`ed file, so a
 *     config that keeps its resource list in `resources.cfg` gets an accurate
 *     report about the half of itself that is in front of us and a `skipped`
 *     line about the half that is not.
 *   - `localStorage` holds the whole form, licence key included, because the
 *     alternative is the competitor's behaviour of losing it on refresh. That
 *     is a real trade and the interface says so in a sentence rather than in a
 *     tooltip, and there is a button that clears it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  Download,
  FileCog,
  FolderOpen,
  Loader2,
  Trash2,
} from "lucide-react";

import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { FindingsList } from "@/components/gta/FindingsList";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  steps as serverCfgSteps,
  finishLabel as serverCfgFinishLabel,
} from "@/components/walkthrough/steps/serverCfg";
import { downloadTextFile } from "@/lib/dom";
import { isManifestFileName, MANIFEST_FILE_NAMES, type GtaResourceSetReport } from "@/lib/gtaKnowledge/analyze";
import { collectResources } from "@/lib/gtaKnowledge/collect";
import {
  DEMO_CFG_PASTE,
  DEMO_INPUT_NOTE,
  DEMO_STATE_LABEL,
  demoDownloadName,
  demoPacksFor,
  DEMO_PACKS,
  type DemoPack,
  type DemoPackUse,
} from "@/lib/gtaKnowledge/demoPacks";
import {
  GAME_BUILDS,
  GAME_BUILDS_STAMP,
  GAME_BUILDS_UPDATED,
  gameBuildLabel,
  isKnownGameBuild,
} from "@/lib/gtaKnowledge/data/gameBuilds.v1";
import { buildDependencyGraph } from "@/lib/gtaKnowledge/dependencies";
import {
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { parseManifest } from "@/lib/gtaKnowledge/manifest";
import { baseName, normalizeName } from "@/lib/gtaKnowledge/normalize";
import { applyEdits } from "@/lib/gtaKnowledge/patch";
import {
  analyzeServerCfg,
  cfgResourcesFromRoots,
  generateServerCfg,
  mergeServerCfg,
  normalizeCfgConfig,
  orderCfgResources,
  parseServerCfg,
  readCfgValue,
  type CfgConfig,
  type CfgExtraSet,
  type CfgResource,
  type OneSyncMode,
  type ParsedCfg,
} from "@/lib/gtaKnowledge/serverCfg";
import type {
  DependencyGraph,
  GtaResourceInput,
  ParsedManifest,
} from "@/lib/gtaKnowledge/types";

import { FreeToolGate } from "./FreeToolGate";
import { FreeToolShell } from "./FreeToolShell";

// ─── Form state ──────────────────────────────────────────────────────────────

/**
 * A directive that can be written on, written off, or not written at all.
 *
 * The third state is not a nicety, it closes the writer's own dishonesty. A form
 * always holds a value, so `configFromForm` used to send a boolean for every
 * hardening flag, and a merge into somebody's maintained config appended
 * `sv_scriptHookAllowed 0` and `sv_endpointPrivacy true` even when their file
 * had never mentioned either — a value the user never typed, written into a file
 * they already run. `CfgConfig` has always accepted `undefined` for "write no
 * line" and `directivesFor` has always honoured it; the *form* was the half that
 * could not express it. `"leave"` is that expression, so no library change is
 * needed here and none is made.
 */
export type CfgTriState = "on" | "off" | "leave";

/** The same third state for `onesync`, which has three on-states rather than one. */
export type OneSyncChoice = OneSyncMode | "leave";

/**
 * Everything the form holds, as text.
 *
 * Numbers are strings and that is deliberate. The whole point of the clamp
 * notice is that the box keeps saying what the user typed while the file says
 * what the artifacts accept; storing a `number` here would mean the box had
 * already been corrected before the notice could describe the correction.
 */
export type ServerCfgForm = {
  hostname: string;
  projectName: string;
  projectDesc: string;
  locale: string;
  tags: string;
  port: string;
  maxClients: string;
  licenseKey: string;
  steamWebApiKey: string;
  rconPassword: string;
  oneSync: OneSyncChoice;
  oneSyncPopulation: CfgTriState;
  /**
   * A build number, or `""` for "leave the server on whatever it defaults to".
   *
   * Any string, deliberately, and not one of `GAME_BUILDS`: a pasted config or a
   * saved form can carry a build newer than our hand-kept table, and the value
   * the control shows has to be the value the file writes whether we have heard
   * of it or not. See the `#cfg-build` select.
   */
  enforceGameBuild: string;
  scriptHookAllowed: CfgTriState;
  endpointPrivacy: CfgTriState;
  mysqlConnectionString: string;
  /** One identifier per line: `license:abc…`, `fivem:1`, `steam:110000…`. */
  admins: string;
  /** One `exec` path per line. */
  execs: string;
  /** One convar per line: `name value`, or `setr name value`. */
  extraSets: string;
  startServerProvided: boolean;
};

export const DEFAULT_SERVER_CFG_FORM: ServerCfgForm = {
  hostname: "My FiveM Server",
  projectName: "My FiveM Server",
  projectDesc: "Roleplay, racing, whatever you actually run",
  locale: "en-US",
  tags: "roleplay, custom",
  port: "30120",
  maxClients: "48",
  licenseKey: "",
  steamWebApiKey: "",
  rconPassword: "",
  oneSync: "on",
  oneSyncPopulation: "off",
  enforceGameBuild: "",
  scriptHookAllowed: "off",
  endpointPrivacy: "on",
  mysqlConnectionString: "",
  admins: "",
  execs: "",
  extraSets: "",
  startServerProvided: true,
};

// ─── Persistence ─────────────────────────────────────────────────────────────

/** Underscored and versioned, as `workshop_resmon_captures_v1` and its neighbours are. */
export const SERVER_CFG_STORAGE_KEY = "workshop_server_cfg_form_v1";

/**
 * Read the saved form, or the defaults.
 *
 * Every access is wrapped, and not defensively-in-general: a private window
 * throws on the *getter* as readily as on the setter, a half-written value
 * throws in `JSON.parse`, and a form saved by an older build has fields this one
 * does not know. All three have the same right answer, which is to start from
 * the defaults rather than to blank the page.
 */
export function readStoredForm(): ServerCfgForm {
  try {
    const raw = window.localStorage.getItem(SERVER_CFG_STORAGE_KEY);
    if (!raw) return DEFAULT_SERVER_CFG_FORM;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return DEFAULT_SERVER_CFG_FORM;
    const stored = parsed as Partial<Record<keyof ServerCfgForm, unknown>>;
    const next: ServerCfgForm = { ...DEFAULT_SERVER_CFG_FORM };
    for (const key of Object.keys(DEFAULT_SERVER_CFG_FORM) as (keyof ServerCfgForm)[]) {
      const value = stored[key];
      const fallback = DEFAULT_SERVER_CFG_FORM[key];
      if (typeof fallback === "boolean" && typeof value === "boolean") {
        (next as Record<string, unknown>)[key] = value;
      } else if (typeof fallback === "string" && typeof value === "string") {
        (next as Record<string, unknown>)[key] = value;
      }
    }
    // The four fields with a closed set of values. The loop above accepts any
    // string whose default is a string, which is right for a hostname and wrong
    // here: a stored `"maybe"` would reach `set onesync` unchecked, and a stored
    // `"true"` in a tri-state would miss every branch of `configFromForm` — the
    // file would write no line while the control showed no state either, which
    // is the same lie the build select used to tell.
    if (
      next.oneSync !== "off" &&
      next.oneSync !== "legacy" &&
      next.oneSync !== "on" &&
      next.oneSync !== "leave"
    ) {
      next.oneSync = DEFAULT_SERVER_CFG_FORM.oneSync;
    }
    for (const key of ["oneSyncPopulation", "scriptHookAllowed", "endpointPrivacy"] as const) {
      if (next[key] !== "on" && next[key] !== "off" && next[key] !== "leave") {
        next[key] = DEFAULT_SERVER_CFG_FORM[key];
      }
    }
    return next;
  } catch {
    return DEFAULT_SERVER_CFG_FORM;
  }
}

/** True when the write landed. False is not an error worth a dialog, but it is worth a sentence. */
export function writeStoredForm(form: ServerCfgForm): boolean {
  try {
    window.localStorage.setItem(SERVER_CFG_STORAGE_KEY, JSON.stringify(form));
    return true;
  } catch {
    return false;
  }
}

function clearStoredForm(): void {
  try {
    window.localStorage.removeItem(SERVER_CFG_STORAGE_KEY);
  } catch {
    /* A browser that refuses the getter refuses this too. Nothing to do. */
  }
}

// ─── Form → CfgConfig ────────────────────────────────────────────────────────

/** `""` means "write no line at all", which is different from a blank value. */
function textOrUndefined(value: string): string | undefined {
  return value.trim() === "" ? undefined : value;
}

/**
 * `undefined` means "write no line at all", which `directivesFor` already honours.
 *
 * The one thing this must not do is collapse `"leave"` to `false`: `false` is a
 * directive written with the value 0, and writing 0 into somebody's config
 * because they declined to have an opinion is the change they declined.
 */
function triStateOrUndefined(value: CfgTriState): boolean | undefined {
  return value === "leave" ? undefined : value === "on";
}

/**
 * A number the file can use, or `undefined`.
 *
 * `undefined` for anything non-numeric rather than `NaN`: `normalizeCfgConfig`
 * would clamp a `NaN` to the minimum and report "you gave NaN", which is a true
 * sentence about a thing the user did not type. The field renders its own note
 * instead — see `numberProblem`.
 */
function numberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** The sentence under a numeric box that does not hold a number. Null when it does. */
function numberProblem(value: string, fallback: number): string | null {
  const trimmed = value.trim();
  if (trimmed === "" || Number.isFinite(Number(trimmed))) return null;
  return `"${trimmed}" is not a number, so this file uses ${fallback}.`;
}

function linesOf(value: string): string[] {
  return value
    .split(/[\r\n]+/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/**
 * `setr vmenu_use_permissions true` and `qb_locale en` both parse.
 *
 * The scope keyword is optional because most people writing a convar into a
 * form box will not type one, and `set` is the right default: `setr` and `sets`
 * send the value to clients and to the server browser respectively, and
 * defaulting to either would quietly publish something.
 */
function parseExtraSets(value: string): CfgExtraSet[] {
  const out: CfgExtraSet[] = [];
  for (const line of linesOf(value)) {
    const tokens = line.split(/\s+/);
    let scope: CfgExtraSet["scope"] = "set";
    let head = tokens[0] ?? "";
    let rest = tokens.slice(1);
    if (head === "set" || head === "setr" || head === "sets") {
      scope = head;
      head = rest[0] ?? "";
      rest = rest.slice(1);
    }
    if (head === "") continue;
    out.push({ name: head, value: rest.join(" ").replace(/^"|"$/g, ""), scope });
  }
  return out;
}

/**
 * The form, plus whatever folder is loaded, as the library's input type.
 *
 * `resources` is only set when a folder was actually read. Passing `[]` for "no
 * folder" would tell `analyzeServerCfg` that it looked at an empty folder, and
 * four of its checks would come back clean instead of coming back skipped.
 */
export function configFromForm(
  form: ServerCfgForm,
  resources: readonly CfgResource[] | undefined,
): CfgConfig {
  return {
    hostname: textOrUndefined(form.hostname),
    projectName: textOrUndefined(form.projectName),
    projectDesc: textOrUndefined(form.projectDesc),
    locale: textOrUndefined(form.locale),
    tags: form.tags.trim() === "" ? undefined : form.tags.split(",").map((tag) => tag.trim()),
    port: numberOrUndefined(form.port),
    maxClients: numberOrUndefined(form.maxClients),
    // The three secrets are passed even when blank, because blank has a meaning
    // the library acts on: it writes the line commented out, which teaches the
    // shape of the directive instead of leaving an absence the reader has to
    // know about to miss.
    licenseKey: form.licenseKey,
    steamWebApiKey: form.steamWebApiKey,
    rconPassword: form.rconPassword,
    oneSync: form.oneSync === "leave" ? undefined : form.oneSync,
    oneSyncPopulation: triStateOrUndefined(form.oneSyncPopulation),
    enforceGameBuild: form.enforceGameBuild.trim() === "" ? null : form.enforceGameBuild.trim(),
    scriptHookAllowed: triStateOrUndefined(form.scriptHookAllowed),
    endpointPrivacy: triStateOrUndefined(form.endpointPrivacy),
    mysqlConnectionString: form.mysqlConnectionString,
    admins: form.admins.trim() === "" ? undefined : linesOf(form.admins),
    execs: form.execs.trim() === "" ? undefined : linesOf(form.execs),
    extraSets: form.extraSets.trim() === "" ? undefined : parseExtraSets(form.extraSets),
    resources,
    startServerProvided: form.startServerProvided,
  };
}

// ─── A pasted config → the form ──────────────────────────────────────────────

/**
 * The form, as the config the user pasted would fill it in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS, AND WHAT IT IS THE FIX FOR
 *
 * `mergeServerCfg` writes every field the configuration it is handed has an
 * opinion about, and `configFromForm` has an opinion about all of them, because
 * a form always holds a value — `DEFAULT_SERVER_CFG_FORM` if nobody typed one.
 * Put those two facts together and import mode rewrote a pasted config's
 * hostname, port, slot count, locale, tags and hardening flags with values the
 * user had never seen, under a line on screen that said everything else was byte
 * for byte what they gave us. That is the honesty rule inverted: the input
 * replaced, and the user told it was preserved.
 *
 * The library is not the place to fix it — `directivesFor` is explicit that a
 * field left undefined writes nothing, which is exactly the defence the surface
 * was walking around. So the surface stops sending values the user never typed:
 * when a config arrives, the form is filled in from it first, and the merge then
 * writes those same values back, which is no change at all.
 *
 * WHAT IT DOES NOT TOUCH
 *
 * Only fields the pasted file actually sets. A config with no `sets tags` line
 * leaves the tags box alone — this reads a file, it does not invent one — which
 * means the merge may still append a directive the file does not have. That is
 * a real change and it is listed, line by line, above the output.
 *
 * `admins`, `exec` lines and extra convars are not read back. The file's `exec`
 * lines stay in the file untouched and the merge only ever adds to them; reading
 * them into a box the user can edit would offer a delete this tool cannot
 * honestly perform.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function formFromCfg(
  cfg: ParsedCfg,
  current: ServerCfgForm,
): { form: ServerCfgForm; fields: string[] } {
  const next: ServerCfgForm = { ...current };
  const fields: string[] = [];

  const note = (directive: string, was: string, now: string): void => {
    if (was === now) return;
    fields.push(`${directive}: your config says ${now}`);
  };

  /** The plain-string boxes. `oneSync` is not one: it has three legal values. */
  type TextField =
    | "hostname"
    | "projectName"
    | "projectDesc"
    | "locale"
    | "tags"
    | "maxClients"
    | "licenseKey"
    | "steamWebApiKey"
    | "rconPassword"
    | "mysqlConnectionString"
    | "enforceGameBuild";

  const text = (directive: string, key: TextField): void => {
    const value = readCfgValue(cfg, directive);
    if (value === null) return;
    note(directive, current[key], value);
    next[key] = value;
  };

  text("sv_hostname", "hostname");
  text("sv_projectName", "projectName");
  text("sv_projectDesc", "projectDesc");
  text("locale", "locale");
  text("tags", "tags");
  text("sv_maxclients", "maxClients");
  text("sv_licenseKey", "licenseKey");
  text("steam_webApiKey", "steamWebApiKey");
  text("rcon_password", "rconPassword");
  text("mysql_connection_string", "mysqlConnectionString");
  text("sv_enforceGameBuild", "enforceGameBuild");

  // The port is not a directive of its own: it is the tail of an endpoint, and
  // an endpoint the host handed somebody is the single worst field to overwrite
  // with a default, because the server starts and nobody can reach it.
  const endpoint = readCfgValue(cfg, "endpoint_add_tcp") ?? readCfgValue(cfg, "endpoint_add_udp");
  const port = endpoint === null ? null : /:(\d{1,5})\s*$/.exec(endpoint.trim())?.[1];
  if (port) {
    note("endpoint_add_tcp", current.port, port);
    next.port = port;
  }

  const oneSync = readCfgValue(cfg, "onesync")?.trim().toLowerCase();
  if (oneSync !== undefined && oneSync !== "") {
    const mode: OneSyncMode | null =
      oneSync === "off" || oneSync === "false" || oneSync === "0"
        ? "off"
        : oneSync === "legacy"
          ? "legacy"
          : oneSync === "on" || oneSync === "true" || oneSync === "1"
            ? "on"
            : null;
    if (mode !== null) {
      note("set onesync", current.oneSync, mode);
      next.oneSync = mode;
    }
  }

  // A directive the pasted file does not set is left alone, exactly as every
  // other field here is: the form keeps whatever state it held, and the user can
  // still answer "leave this line out" if they do not want us appending one.
  const flag = (
    directive: string,
    key: "oneSyncPopulation" | "scriptHookAllowed" | "endpointPrivacy",
  ): void => {
    const value = readCfgValue(cfg, directive)?.trim().toLowerCase();
    if (value === undefined || value === "") return;
    const state: CfgTriState =
      value === "1" || value === "true" || value === "yes" ? "on" : "off";
    note(directive, current[key], state);
    next[key] = state;
  };
  flag("onesync_population", "oneSyncPopulation");
  flag("sv_scriptHookAllowed", "scriptHookAllowed");
  flag("sv_endpointPrivacy", "endpointPrivacy");

  return { form: next, fields };
}

// ─── Presets ─────────────────────────────────────────────────────────────────

/**
 * Four starting points, each of which says what it does not do.
 *
 * A preset that silently implied "this config now runs ESX" would be the same
 * confident half-answer the rest of this layer is written against: the config is
 * a third of it, the folder is the rest, and load order between `oxmysql`,
 * `es_extended` and everything that calls them is the part that actually breaks.
 * So every preset carries a sentence naming what still has to be true, and each
 * one patches only the fields it has an opinion about — a preset click never
 * clears a hostname or a licence key somebody typed.
 */
type Preset = {
  key: string;
  label: string;
  note: string;
  patch: Partial<ServerCfgForm>;
};

export const SERVER_CFG_PRESETS: readonly Preset[] = [
  {
    key: "bare",
    label: "Bare server",
    note:
      "The stock scaffold: the artifacts' own resources, OneSync on, no database. This is what a " +
      "server that runs nothing but the FiveM defaults looks like.",
    patch: {
      oneSync: "on",
      oneSyncPopulation: "off",
      maxClients: "48",
      mysqlConnectionString: "",
      extraSets: "",
      execs: "",
      startServerProvided: true,
    },
  },
  {
    key: "esx",
    label: "ESX",
    note:
      "ESX needs a database, and it needs oxmysql started before es_extended and es_extended before " +
      "anything that calls it. This preset fills in the config; it does not install the framework " +
      "and it cannot order resources it has not seen — drop your resources folder for that.",
    patch: {
      oneSync: "on",
      maxClients: "64",
      tags: "esx, roleplay, economy",
      mysqlConnectionString: "mysql://user:password@localhost/es_extended?charset=utf8mb4",
      startServerProvided: true,
    },
  },
  {
    key: "qbcore",
    label: "QBCore",
    note:
      "QBCore needs a database, and qb-core has to start after oxmysql and before every qb-* " +
      "resource. This preset fills in the config; the order comes from the folder, so drop your " +
      "resources folder in.",
    patch: {
      oneSync: "on",
      maxClients: "64",
      tags: "qbcore, roleplay, economy",
      mysqlConnectionString: "mysql://user:password@localhost/qbcore?charset=utf8mb4",
      startServerProvided: true,
    },
  },
  {
    key: "vmenu",
    label: "vMenu",
    note:
      "vMenu keeps its permissions in its own permissions.cfg, which this tool does not write — the " +
      "exec line below points at where vMenu ships it. No database is needed. Check the two convars " +
      "against vMenu's own config before trusting them; they are a starting point, not its manual.",
    patch: {
      oneSync: "on",
      maxClients: "32",
      tags: "vmenu, freeroam",
      mysqlConnectionString: "",
      execs: "vMenu/config/permissions.cfg",
      extraSets: "setr vmenu_use_permissions true\nsetr vmenu_menu_staff_only false",
      startServerProvided: true,
    },
  },
];

// ─── The folder ──────────────────────────────────────────────────────────────

export type LoadedFolder = {
  /** In discovery order. The ordered list is derived, and derived once. */
  resources: CfgResource[];
  /** Bracket folders present, lower-cased, `[cars]`. */
  categories: string[];
  /** `null` when nothing in the drop declared or implied a dependency. */
  graph: DependencyGraph | null;
  /** How many distinct archives or picks the drop came from. */
  origins: number;
  /** Everything the intake and the collector could not read, in their words. */
  truncated: string[];
};

/**
 * A collected resource, in the shape `buildDependencyGraph` asks for.
 *
 * This is the one adapter in the file, and it is an adapter rather than an
 * analysis: `analyzeResourceSet` would hand back the same graph, but it would
 * first run every vehicle-meta, stream-asset and manifest rule over a folder
 * that may hold several thousand files, to produce findings this page does not
 * render. The two facts it needs — which manifest file is the manifest, and
 * which `.lua` files are scripts rather than manifests — are read through the
 * same exported constant and the same exported predicate `analyze.ts` uses, so
 * there is nothing here that can drift into a second answer.
 */
function graphInputOf(input: GtaResourceInput): {
  name: string;
  manifest: ParsedManifest | null;
  scripts: { file: string; source: string }[];
} {
  let manifest: ParsedManifest | null = null;
  for (const candidate of MANIFEST_FILE_NAMES) {
    const source = input.texts.get(candidate);
    if (source === undefined) continue;
    manifest = parseManifest(candidate, source);
    break;
  }
  const scripts: { file: string; source: string }[] = [];
  for (const [path, source] of input.texts) {
    if (path.endsWith(".lua") && !isManifestFileName(baseName(path))) {
      scripts.push({ file: path, source });
    }
  }
  return { name: input.name, manifest, scripts };
}

/**
 * Sentences about folders that share a resource name.
 *
 * This is the one thing the config side genuinely cannot express, and it has to
 * be said here because nothing below can say it. A `server.cfg` names resources
 * by their folder name, so two folders both called `police` are one name to the
 * server: `orderCfgResources` keys on that name and carries the first, and an
 * `ensure police` line can only ever mean one of them. That is correct — there
 * is no config that starts both — but it means the second folder disappears
 * between the drop and the ensure block, and a tool that let it disappear
 * quietly would be reporting a clean, perfectly ordered file for a folder that
 * cannot start. So the drop is inspected by root before the collapse happens,
 * and both paths are named.
 *
 * It is not a finding and deliberately not given one of the eleven `cfg.*`
 * codes: those are the library's and are fixed. It goes into the panel's
 * `truncated` line, which is where "what did not make it through" belongs.
 */
function duplicateNameNotices(drop: ResourceDrop): string[] {
  const byName = new Map<string, string[]>();
  for (const resource of drop.resources) {
    const id = normalizeName(resource.name);
    if (id === "") continue;
    const roots = byName.get(id);
    if (roots) roots.push(resource.root);
    else byName.set(id, [resource.root]);
  }

  const notices: string[] = [];
  for (const [id, roots] of byName) {
    if (roots.length < 2) continue;
    notices.push(
      `${roots.length} folders in this drop are all called ${id} (${roots.join(", ")}). A ` +
        // No "below": this sentence renders twice — under the drop zone in the
        // left input column, and again inside the rail's `Not checked:` region
        // via `truncated`. The ensure block is to the right of the first and
        // above the second from `lg` up, so the only direction that is true at both
        // sites and every width is none.
        "server.cfg names a resource by its folder name, so only one of them can be started — the " +
        "ensure block holds the first and the others are not started at all. Rename them " +
        "before this folder goes anywhere near a server.",
    );
  }
  return notices;
}

/**
 * A drop, turned into everything the config side needs.
 *
 * `root` and `origin` are both passed into `collectResources`, and that is the
 * whole reason Phase 0 exists: two vendor packs both called `police` keyed on
 * their name fold into one resource and the tool reports a folder with one
 * `police` in it, ordered perfectly, that cannot start.
 */
export async function loadFolder(drop: ResourceDrop): Promise<LoadedFolder> {
  const collected = await collectResources(
    drop.resources.map((resource) => ({
      name: resource.name,
      files: resource.files,
      root: resource.root,
      origin: drop.origins.get(resource.root),
    })),
  );

  const graph = buildDependencyGraph(collected.inputs.map(graphInputOf));
  const resources = cfgResourcesFromRoots(drop.resources.map((resource) => resource.root));
  const categories = [
    ...new Set(
      resources
        .map((resource) => resource.category?.toLowerCase())
        .filter((category): category is string => typeof category === "string"),
    ),
  ];

  return {
    resources,
    categories,
    graph,
    origins: new Set(drop.origins.values()).size,
    truncated: [...drop.truncated, ...collected.truncated, ...duplicateNameNotices(drop)],
  };
}

// ─── Getting the file out of the tab ─────────────────────────────────────────

const EMPTY_GRAPH: DependencyGraph = {
  nodes: [],
  edges: [],
  cycles: [],
  order: [],
  constraints: [],
};

// ─── Small controls ──────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full rounded-xl border border-border bg-background px-3 py-2 font-mono text-sm text-foreground outline-none transition-colors focus:border-primary/50";

/**
 * The two selects, in the sans face rather than mono.
 *
 * Their captions are sentences — "Off — no state awareness, and a hard 32-slot
 * cap whatever sv_maxclients says" — and a closed `<select>` cannot wrap, so
 * in a ~350px field the mono face cut that one at "and a h", before the cap it
 * exists to warn about. Sans fits roughly half as much again in the same box.
 * The typed boxes stay mono because what goes in them is directive syntax.
 */
const SELECT_CLASS = INPUT_CLASS.replace("font-mono", "font-sans");

const CHIP_CLASS =
  "rounded-lg border border-border bg-card px-2 py-1 font-mono text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground";

/**
 * One labelled control, and three text slots that are three on purpose.
 *
 * They are three registers, and exactly one of them is ever allowed to collapse:
 *
 *   - `hint` — what this directive *is*. Domain explanation, one short clause.
 *     Anything longer lives in the section's "What these do" disclosure, which
 *     is the only disclosure mechanism in this file.
 *   - `note` — what this tool does not do to this field, in words. This is the
 *     honesty slot and it is always visible. A limit behind a closed triangle
 *     has not been told, and the test helper here reads `container.textContent`,
 *     which cannot tell a closed `<details>` from an open one — so the rule has
 *     to hold in the markup rather than in an assertion.
 *   - `problem` — something wrong with the value in the box right now.
 */
function Field({
  id,
  label,
  hint,
  note,
  problem,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  note?: string | null;
  problem?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs leading-5 text-muted-foreground">{hint}</p>}
      {note && <p className="text-xs leading-5 text-muted-foreground">{note}</p>}
      {problem && <p className="text-xs leading-5 text-warning">{problem}</p>}
    </div>
  );
}

/**
 * A checkbox, for the one option that is a tool option rather than a directive.
 *
 * `hint` is optional, matching `Field`. It used to be required by the type,
 * which is how twenty controls came to carry twenty hints: the component asked
 * for a sentence and every call site found one to give it.
 *
 * `disabled` carries `note` with it, because a control that cannot be changed
 * has to say why in the same glance — see `cfg-server-provided` in import mode.
 */
function Toggle({
  id,
  label,
  hint,
  note,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  note?: string | null;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className={`flex items-start gap-2.5 ${disabled ? "opacity-60" : ""}`}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
        />
        <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
          <span className="block text-xs font-medium text-foreground">{label}</span>
          {hint && (
            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{hint}</span>
          )}
        </label>
      </div>
      {note && <p className="text-xs leading-5 text-warning">{note}</p>}
    </div>
  );
}

/**
 * On, off, or no line at all — all three visible at once.
 *
 * A three-option `<select>` costs two clicks to see the two alternatives and
 * shows one state at a time; a segmented control shows every state and the
 * current one in a glance, which matters most for the third state because
 * nobody goes looking for an option they do not know exists. The third state is
 * the one that stops this tool writing a value into a config the user never
 * typed — see `CfgTriState`.
 *
 * WHY THE KEYBOARD HANDLER IS NOT OPTIONAL HERE
 *
 * `role="radiogroup"` over `role="radio"` children is a promise about the
 * keyboard, not a label: NVDA and JAWS announce "radio group, 1 of 3" and then
 * tell the user to change the selection with the arrow keys. Shipped without
 * the handler, the arrows did nothing and all three buttons took a tab stop, so
 * the three choosers added nine stops to a form this pass exists to shorten and
 * were unreachable by the gesture the role advertises. The pattern is the whole
 * contract: arrows move *and* select, and the group is one tab stop because
 * only the checked option carries `tabIndex={0}`.
 *
 * Buttons rather than three `<input type="radio">`, which would get all of this
 * from the browser: the visual shell is a segmented control whose options fill
 * the row, and a native radio's `name` grouping would have to be threaded
 * through a component that is rendered four times on one form. Nothing in the
 * clamp or honesty reasoning depends on either choice — this is the cheaper of
 * two correct options, and it is only correct with the handler below.
 */
function TriStateControl({
  id,
  label,
  hint,
  onLabel,
  offLabel,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  onLabel: string;
  offLabel: string;
  value: CfgTriState;
  onChange: (value: CfgTriState) => void;
}) {
  const options: readonly { state: CfgTriState; label: string }[] = [
    { state: "on", label: onLabel },
    { state: "off", label: offLabel },
    { state: "leave", label: "Leave this line out" },
  ];
  const selected = Math.max(
    0,
    options.findIndex((option) => option.state === value),
  );

  /**
   * Arrow keys move and select, and they wrap.
   *
   * Focus is moved by id rather than through a ref array because the ids are
   * already the suite's handle on these buttons (`#cfg-scripthook-leave`), so
   * one spelling serves the test, the label association and this.
   */
  const move = (delta: number) => {
    const next = options[(selected + delta + options.length) % options.length];
    onChange(next.state);
    const el = document.getElementById(`${id}-${next.state}`);
    if (el instanceof HTMLElement) el.focus();
  };

  return (
    <div className="min-w-0 space-y-1.5">
      <span id={`${id}-label`} className="block text-xs font-medium text-foreground">
        {label}
      </span>
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        className="flex flex-wrap gap-1 rounded-xl border border-border bg-background p-1"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
          } else if (event.key === "Home") {
            event.preventDefault();
            move(-selected);
          } else if (event.key === "End") {
            event.preventDefault();
            move(options.length - 1 - selected);
          }
        }}
      >
        {options.map((option, index) => (
          <button
            key={option.state}
            id={`${id}-${option.state}`}
            type="button"
            role="radio"
            aria-checked={value === option.state}
            // One tab stop for the group, which is the other half of the
            // radiogroup contract: three stops per chooser is nine extra stops
            // across this form for a control that holds one value.
            tabIndex={index === selected ? 0 : -1}
            onClick={() => onChange(option.state)}
            className={`min-w-0 flex-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
              value === option.state
                ? "bg-primary/15 text-foreground"
                : "text-muted-foreground hover:bg-muted/40"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {hint && <p className="text-xs leading-5 text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * A text input with a `<datalist>`, and optionally a row of one-click values.
 *
 * Deliberately not a `<select>`, and `sv_maxclients` is the reason. That field
 * is what the entire clamp notice exists for: the box has to be able to hold
 * `999999` so the file can say 2048 and the panel can say why, and a `<select>`
 * cannot hold a value that is not one of its options. A `<datalist>` suggests
 * and cannot rewrite — it is an attribute on a text input, so nothing in the
 * clamp path is touched — and the chips perform the same mutation `onChange`
 * does. `type="text"` with `inputMode="numeric"` rather than `type="number"`
 * for the same family of reason: a number input lets the browser swallow
 * "sixty four" before `numberProblem` can say it is not a number.
 */
function SuggestInput({
  id,
  value,
  suggestions,
  chips,
  onChip,
  inputMode,
  onChange,
}: {
  id: string;
  value: string;
  /** Whole-value suggestions. Omitted for a field whose value is a list, where replacing it would be wrong. */
  suggestions?: readonly string[];
  chips?: readonly string[];
  onChip?: (chip: string) => void;
  inputMode?: "numeric";
  onChange: (value: string) => void;
}) {
  const listId = `${id}-suggestions`;
  const hasList = suggestions !== undefined && suggestions.length > 0;
  return (
    <div className="min-w-0 space-y-1.5">
      <input
        id={id}
        className={INPUT_CLASS}
        value={value}
        list={hasList ? listId : undefined}
        spellCheck={false}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
      {hasList && (
        <datalist id={listId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      )}
      {chips && onChip && (
        <div className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <button key={chip} type="button" onClick={() => onChip(chip)} className={CHIP_CLASS}>
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The overflow from this section's hints, one click away.
 *
 * One disclosure per section rather than one per control. Twenty triangles over
 * the same three hundred words is two mechanisms doing one job, and four or five
 * is the better answer to "too vertical" anyway. Nothing in here is ever an
 * honesty statement — those are `note` and `problem`, which do not collapse — so
 * a reader who never opens this is missing an explanation, not a limit.
 *
 * The markup is `BadgeLegend`'s from `SpawnIndexClient`, deliberately unchanged,
 * so this is a pattern the codebase already ships rather than a new component
 * with its own behaviour.
 */
function WhatTheseDo({ entries }: { entries: readonly { term: string; detail: string }[] }) {
  return (
    <details className="group rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <span>What these do</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>
      <dl className="mt-3 space-y-2">
        {entries.map((entry) => (
          <div key={entry.term} className="flex flex-wrap items-baseline gap-2">
            <dt className="font-mono text-xs text-foreground">{entry.term}</dt>
            <dd className="min-w-0 flex-1 text-xs leading-6 text-muted-foreground">
              {entry.detail}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </h3>
  );
}

// ─── Suggestion lists ────────────────────────────────────────────────────────

/**
 * The locales that actually appear on FiveM servers. Suggestions, not a table.
 *
 * A `<datalist>` rather than a `<select>`, for the reason `gameBuilds.v1.ts`
 * spends its provenance header on: nothing in this repo enumerates FiveM
 * locales, so a closed list here would be a second hand-kept table with nothing
 * in the tree to cross-check it against. A suggestion list claims no
 * completeness and accepts anything typed over it, so there is no
 * value-not-in-options bug to fix later — which is exactly the bug `#cfg-build`
 * had.
 */
const LOCALE_SUGGESTIONS = [
  "en-US",
  "en-GB",
  "de-DE",
  "fr-FR",
  "es-ES",
  "pt-BR",
  "pl-PL",
  "nl-NL",
  "it-IT",
  "sv-SE",
  "tr-TR",
  "cs-CZ",
] as const;

/**
 * The slot counts people run. `32` is the OneSync-off cap, `2048` the ceiling.
 *
 * Suggestions and chips, never options: the value this field has to be able to
 * hold is `999999`. See `SuggestInput`.
 */
const MAX_CLIENT_TIERS = ["32", "48", "64", "100", "128", "2048"] as const;

/**
 * Tags that append to the box rather than replacing it.
 *
 * `tags` is a marketing string and the server browser accepts anything, so a
 * dropdown here would claim a taxonomy FiveM does not have. These are additive
 * and non-constraining; the box stays free text.
 */
const TAG_SUGGESTIONS = [
  "roleplay",
  "economy",
  "racing",
  "drift",
  "freeroam",
  "whitelist",
  "esx",
  "qbcore",
] as const;

/**
 * The sentinel that reveals a free-text build box.
 *
 * `GAME_BUILDS` is hand-kept and says so, so a user on artifacts newer than our
 * last transcription has to have some way to type their build. Without this the
 * only values the control can express are the fifteen we happen to know, which
 * is the failure mode that table's own provenance essay predicts.
 */
const OTHER_BUILD = "__other";

// ─── The island ──────────────────────────────────────────────────────────────

export function ServerCfgClient({
  intro,
  children,
}: {
  /** The page's `<h1>` and standfirst, rendered on the server. */
  intro?: ReactNode;
  /** The SEO body copy, rendered on the server, placed below the tool. */
  children?: ReactNode;
}) {
  const [form, setForm] = useState<ServerCfgForm>(DEFAULT_SERVER_CFG_FORM);
  const [storageReady, setStorageReady] = useState(false);
  const [storageRefused, setStorageRefused] = useState(false);
  const [presetNote, setPresetNote] = useState<string | null>(null);

  /**
   * Whether the build select is showing its free-text escape hatch.
   *
   * Mode rather than value, because entering the mode must not change the file:
   * whatever `form.enforceGameBuild` holds keeps being written, and the text box
   * beside the select shows that same value. The two can never disagree.
   */
  const [otherBuild, setOtherBuild] = useState(false);

  const [folder, setFolder] = useState<LoadedFolder | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  /**
   * Whether the loaded folder is a built-in sample pack.
   *
   * Set by `runIntake`, which every gesture goes through, so a real folder
   * clears it without anything remembering to.
   */
  const [demoFolder, setDemoFolder] = useState(false);
  /** Raised by a demo button only. See the mount at the bottom of this component. */
  const [tourSignal, setTourSignal] = useState(0);

  const [existingText, setExistingText] = useState("");
  const [copied, setCopied] = useState(false);

  /** What arrived from a pasted config, and the form as it was before it did. */
  const [seed, setSeed] = useState<{ fields: string[]; previous: ServerCfgForm } | null>(null);
  const seeded = useRef(false);

  const folderInput = useRef<HTMLInputElement | null>(null);
  const archiveInput = useRef<HTMLInputElement | null>(null);
  const cfgInput = useRef<HTMLInputElement | null>(null);

  // Read on the client only. Seeding state from `localStorage` during render
  // would make the server's HTML and the first client render disagree, which
  // React discards the whole tree over.
  useEffect(() => {
    setForm(readStoredForm());
    setStorageReady(true);
  }, []);

  useEffect(() => {
    if (!storageReady) return;
    setStorageRefused(!writeStoredForm(form));
  }, [form, storageReady]);

  const update = useCallback(<K extends keyof ServerCfgForm>(key: K, value: ServerCfgForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  /**
   * A tag chip appends to the box; it does not replace what is in it.
   *
   * Additive because the box is free text and the chips are not a taxonomy. The
   * duplicate check is on the trimmed, lower-cased value so clicking `esx` twice
   * does not write `esx, esx` and hand `normalizeCfgConfig` a duplicate to
   * report as an adjustment the user did not cause.
   */
  const appendTag = useCallback((tag: string) => {
    setForm((current) => {
      const present = current.tags
        .split(",")
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry !== "");
      if (present.includes(tag)) return current;
      const head = current.tags.trim().replace(/,+$/, "");
      return { ...current, tags: head === "" ? tag : `${head}, ${tag}` };
    });
  }, []);

  // ── The one ordering, computed once ──
  //
  // See the header. `generateServerCfg` would order for itself, `mergeServerCfg`
  // would not order at all, and `analyzeServerCfg` would judge whatever it was
  // handed — three answers to one question unless the answer is computed here
  // and passed to all three.
  const ordered = useMemo(
    () => (folder ? orderCfgResources(folder.resources, folder.graph) : null),
    [folder],
  );

  const config = useMemo(
    () => configFromForm(form, ordered?.resources),
    [form, ordered],
  );

  const adjustments = useMemo(() => normalizeCfgConfig(config).adjustments, [config]);

  const existing = useMemo<ParsedCfg | null>(
    () => (existingText.trim() === "" ? null : parseServerCfg(existingText, { file: "server.cfg" })),
    [existingText],
  );

  /**
   * Fill the form in from a pasted config.
   *
   * See `formFromCfg`. It runs once when a config arrives rather than on every
   * keystroke in the textarea, because re-seeding would undo an edit the user
   * made in a box afterwards — the same overwrite in miniature. A button offers
   * it again, and `seed.previous` is what the Undo button puts back.
   */
  const seedFromCfg = useCallback(
    (cfg: ParsedCfg) => {
      const result = formFromCfg(cfg, form);
      setSeed(result.fields.length === 0 ? null : { fields: result.fields, previous: form });
      if (result.fields.length > 0) setForm(result.form);
    },
    [form],
  );

  useEffect(() => {
    if (!existing) {
      seeded.current = false;
      setSeed(null);
      return;
    }
    if (seeded.current) return;
    seeded.current = true;
    seedFromCfg(existing);
  }, [existing, seedFromCfg]);

  const analysis = useMemo(
    () =>
      analyzeServerCfg({
        cfg: existing,
        config,
        resources: folder?.resources,
        categories: folder?.categories,
        graph: folder?.graph ?? null,
      }),
    [existing, config, folder],
  );

  /**
   * The file, and what kind of file it is.
   *
   * With a config imported, the output is a patch of that config and nothing
   * else — regenerating would be the thing the brief calls a one-shot
   * scaffolder, and it would throw away the `exec` lines, the comments and the
   * convars this tool has no opinion about. Refused edits are surfaced rather
   * than dropped: `applyEdits` refuses an edit whose `expect` no longer matches,
   * and a merge that quietly applied nine of eleven edits and said "done" is a
   * file the user would ship believing it was complete.
   *
   * `changes` is that same rule one step further on. A count of changed lines
   * over somebody's maintained config is not a report, it is a reassurance: it
   * cannot be checked, and it reads as "nothing you care about moved" whatever
   * moved. Every edit carries the text it expected to find and the text it
   * writes, so each rewrite is rendered as both and each appended line as
   * itself.
   */
  const output = useMemo(() => {
    if (!existing) {
      return {
        mode: "generated" as const,
        text: generateServerCfg(config, folder?.graph ?? undefined),
        refused: [] as string[],
        applied: 0,
        changes: [] as { before: string | null; after: string }[],
      };
    }
    const edits = mergeServerCfg(existing, config);
    const result = applyEdits(existingText, edits);
    const changes: { before: string | null; after: string }[] = [];
    for (const edit of result.applied) {
      if (edit.start !== edit.end) {
        changes.push({
          before: typeof edit.expect === "string" ? edit.expect.trim() : null,
          after: edit.replacement.trim(),
        });
        continue;
      }
      // The single insertion at the end of the file, section headings and all.
      // The headings are comments and change nothing about the server, so only
      // the lines that do are listed.
      for (const line of edit.replacement.split("\n")) {
        const trimmed = line.trim();
        if (trimmed === "" || trimmed.startsWith("#")) continue;
        changes.push({ before: null, after: trimmed });
      }
    }
    return {
      mode: "merged" as const,
      text: result.text,
      refused: result.rejected.map(({ reason }) => reason),
      applied: result.applied.length,
      changes,
    };
  }, [existing, existingText, config, folder]);

  const report = useMemo<GtaResourceSetReport>(
    () => ({
      resources: [],
      findings: analysis.findings,
      dependencies: folder?.graph ?? EMPTY_GRAPH,
      stream: [],
      skipped: analysis.skipped,
    }),
    [analysis, folder],
  );

  // ── Intake ──

  const runIntake = useCallback(async (load: () => Promise<ResourceDrop>, fromDemo = false) => {
    setFolderBusy(true);
    setFolderError(null);
    setDemoFolder(fromDemo);
    try {
      setFolder(await loadFolder(await load()));
    } catch (error) {
      // A refusal from the intake caps, a zip that is not a zip, a directory the
      // browser would not walk. The message is shown rather than swallowed: a
      // drop that produced nothing and said nothing is indistinguishable from a
      // folder with no resources in it.
      setFolderError(
        error instanceof Error
          ? `That folder could not be read: ${error.message}`
          : "That folder could not be read, and the browser gave no reason.",
      );
      setFolder(null);
    } finally {
      setFolderBusy(false);
    }
  }, []);

  const onDrop = useCallback(
    (transfer: DataTransferLike | null) => {
      if (!transfer) return;
      void runIntake(() => fromDataTransfer(transfer));
    },
    [runIntake],
  );

  // ── The worked examples ──
  //
  // Two folders and a paste. Fed through `runIntake` and through `setExistingText`
  // — the same two entry points a user's own drop and paste take — so nothing
  // here can produce a result the real pipeline would not.

  const demoPacks = useMemo(() => demoPacksFor("server-cfg"), []);

  const loadDemoPack = useCallback(
    async (pack: DemoPack, use: DemoPackUse) => {
      if (use.gesture === "archives") {
        const files = await pack.archives();
        await runIntake(() => fromArchives(files), true);
      } else {
        const list = pack.pick();
        await runIntake(() => fromDirectoryPick(list), true);
      }
      setTourSignal((signal) => signal + 1);
    },
    [runIntake],
  );

  /**
   * The paste demo, which loads its pack as well.
   *
   * `DEMO_CFG_PASTE.needsPack` names the folder this config composes with, and
   * offering the paste alone would be a demo that cannot show the thing it is
   * for: the pasted `ensure` block is in the broken order, and the reordering
   * diff only exists when there is a folder whose dependency graph says so. So
   * this loads both, in the order a user would — folder, then paste — and lets
   * the same `useEffect` that seeds the form from a real paste do its work.
   */
  const loadDemoPaste = useCallback(async () => {
    const pack = DEMO_PACKS.find((entry) => entry.id === DEMO_CFG_PASTE.needsPack);
    const use = pack?.uses["server-cfg"];
    if (pack && use) {
      if (use.gesture === "archives") {
        const files = await pack.archives();
        await runIntake(() => fromArchives(files), true);
      } else {
        const list = pack.pick();
        await runIntake(() => fromDirectoryPick(list), true);
      }
    }
    setExistingText(DEMO_CFG_PASTE.text);
    setTourSignal((signal) => signal + 1);
  }, [runIntake]);

  /**
   * Whether anything on screen came from a sample.
   *
   * The paste half is derived rather than remembered: a flag would have to be
   * cleared when the user edits the box, and a stale "this is a sample" over a
   * config somebody actually pasted is the same lie in the other direction.
   * Comparing the text is exact and cannot go stale.
   */
  const demoPaste = existingText === DEMO_CFG_PASTE.text;
  const fromDemo = demoFolder || demoPaste;

  // ── The game build control ──
  //
  // The bug this replaces: the select's options were `""` plus the fifteen rows
  // of a hand-kept table, while `readStoredForm` restores any string and
  // `formFromCfg` seeds whatever a pasted config carried. A config saying
  // `sv_enforceGameBuild 4012` therefore put "4012" in form state with no
  // matching `<option>`, so the box rendered its first option — "Leave it to the
  // server (no line written)" — while the file underneath wrote 4012. The
  // interface said one thing and the output did another, in the one control this
  // tool is built around.
  //
  // The library was never wrong about this: `gameBuildLabel` returns the bare
  // value for a build it does not know, and `gameBuildsBehind` returns `null`
  // rather than "stale", both because a build newer than this file is not an
  // error. Only the control lied, so only the control is fixed.
  const buildValue = form.enforceGameBuild.trim();
  const buildUnknown = buildValue !== "" && !isKnownGameBuild(buildValue);
  const buildProblem = otherBuild
    ? buildValue === ""
      ? "No build typed, so no sv_enforceGameBuild line is written."
      : null
    : buildUnknown
      ? `${buildValue} is not in our build table (hand-kept, checked ${GAME_BUILDS_UPDATED}), so nothing here can tell you whether it is current. The line is written exactly as it stands.`
      : null;

  const serverProvidedNote = existing
    ? "Inert on a pasted config — no start lines are added."
    : null;

  return (
    <FreeToolShell current="server-cfg" width="wide">
      {intro}

      {/* Interactive island — gated behind a free account; `intro` above and
          `children` (the upsell + reading zone) below stay outside the gate so
          the page's SEO content still prerenders and still shows for a
          signed-out visitor. */}
      <FreeToolGate path="/free/server-cfg">
      <div className="not-prose space-y-6">
        <p className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-xs leading-6 text-muted-foreground">
          Everything below runs in this browser tab. No folder, config or licence key is uploaded
          anywhere, and there is no server to upload it to. A clean report is a static check of the
          files you gave it — it is not proof the server boots.{" "}
          <span className="text-foreground">{GAME_BUILDS_STAMP}.</span>
        </p>

        {/* The permanent way into the tour — see `ShowTourButton`.
            Full width at the top rather than inside the drop zone, because this
            is the one of the four tools whose primary mode never involves a
            folder at all: a user filling in an empty form has no drop zone in
            front of them and had, before this, no route to a tutorial. The
            sample-pack buttons live inside the zone and disappear once input
            lands; this does not. */}
        <div className="flex flex-wrap items-center gap-3">
          <ShowTourButton
            testId="cfg-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {/* ── Region split ──
            Inputs on the left, the output rail on the right, at `lg` and up. The
            rail is the point: the panel that explains why the file says 2048
            when the box says 999999 used to render about two thousand pixels
            below the box that triggered it, and a correct sentence nobody can
            see at the moment it matters is a mechanism that is present and does
            not work. Below `lg` this is one column and the rail is an ordinary
            stacked section.

            WHY `lg` AND NOT `xl` — decided from screenshots, not class names.
            The spec left this open and shipped `xl`, on the worry that a
            `col-span-4` rail at 1024px is ~330px and cannot show a line. Two
            things were measured with a real 292-resource folder and a pasted
            config loaded. At 1280px the `xl` rail renders 390px wide and reads
            fine: the clamp notice is whole, change lines wrap once at most, and
            the `<pre>` scrolls sideways inside itself, which it does at every
            width. And at 1024px, with no rail, the file and the clamp notice
            started ~3,200px below the slot box on an 18,600px page — the exact
            defect the rail exists for, on an ordinary laptop width. So the rail
            takes five columns at `lg` rather than four, which is ~386px at
            1024px, the same measure `xl` already proved out, and the inputs
            take the other seven with groups one-up. `xl` goes back to 8/4
            (390px) and `2xl` to 7/5 as before. */}
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-7 xl:col-span-8 2xl:col-span-7">
            {/* Below `lg`, groups are full width and their fields are 2-up. At
                `lg` the rail takes five columns and the inputs' ~550px holds
                one group with fields 1-up — 2-up there is ~245px a field, and
                the onesync and build selects cannot show their captions in
                that. At `xl` groups go 2-up and fields stay 1-up inside each
                ~390px group, which is what puts the drop zone beside the keys
                and hardening stack.

                Two placed columns rather than `columns-2`, because CSS
                multi-column reflows children between columns by height and tab
                order would follow it. Here the DOM order is column A then
                column B, so keyboard tab runs down the left and then down the
                right, which is what a two-column form reads as.

                Column A is presets → server browser → the folder-and-paste
                pair; column B is slots → keys → hardening. The paste box is in
                A rather than at the bottom of B because it is the other half of
                the folder's task, and DOM order is what a single column below
                `xl` collapses to: with it in B, dropping a folder and then
                pasting the config it belongs to meant scrolling past fifteen
                controls. Keys and hardening still sit beside the drop zone at
                `xl`, which is what the layout pass was for. */}
            <div className="grid gap-5 xl:grid-cols-2 xl:items-start">
              <div className="min-w-0 space-y-5">
                {/* ── Presets ── */}
                <section className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-5">
                  <SectionHeading>Start from</SectionHeading>
                  <div className="flex flex-wrap gap-2">
                    {SERVER_CFG_PRESETS.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => {
                          setForm((current) => ({ ...current, ...preset.patch }));
                          setPresetNote(preset.note);
                        }}
                        className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted/40"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {presetNote && (
                    <p className="text-xs leading-6 text-muted-foreground">{presetNote}</p>
                  )}
                </section>

                {/* ── What the server browser shows ── */}
                <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5">
                  <SectionHeading>What the server browser shows</SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                    <Field
                      id="cfg-hostname"
                      label="sv_hostname"
                      hint="The name in the server list."
                    >
                      <input
                        id="cfg-hostname"
                        className={INPUT_CLASS}
                        value={form.hostname}
                        spellCheck={false}
                        onChange={(event) => update("hostname", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-project-name"
                      label="sv_projectName"
                      hint="The project title the FiveM client shows."
                    >
                      <input
                        id="cfg-project-name"
                        className={INPUT_CLASS}
                        value={form.projectName}
                        spellCheck={false}
                        onChange={(event) => update("projectName", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-project-desc"
                      label="sv_projectDesc"
                      hint="One line under the title in the browser."
                    >
                      <input
                        id="cfg-project-desc"
                        className={INPUT_CLASS}
                        value={form.projectDesc}
                        spellCheck={false}
                        onChange={(event) => update("projectDesc", event.target.value)}
                      />
                    </Field>
                    <Field id="cfg-tags" label="tags" hint="Comma separated.">
                      {/* Chips but no `<datalist>`: this box holds a comma-
                          separated list, and a whole-value suggestion would
                          offer to replace the list with one tag. */}
                      <SuggestInput
                        id="cfg-tags"
                        value={form.tags}
                        chips={TAG_SUGGESTIONS}
                        onChip={appendTag}
                        onChange={(value) => update("tags", value)}
                      />
                    </Field>
                    <Field id="cfg-locale" label="locale" hint="e.g. en-US, de-DE.">
                      <SuggestInput
                        id="cfg-locale"
                        value={form.locale}
                        suggestions={LOCALE_SUGGESTIONS}
                        onChange={(value) => update("locale", value)}
                      />
                    </Field>
                  </div>
                  <WhatTheseDo
                    entries={[
                      {
                        term: "sv_hostname",
                        detail:
                          "Quotes, # and backslashes in the name are escaped for you, so the line cannot break in two and turn the rest of the name into a command.",
                      },
                      {
                        term: "tags",
                        detail:
                          "A comma inside a single tag would split it, so commas inside one are turned into spaces. FiveM keeps 32 of them.",
                      },
                      {
                        term: "locale",
                        detail:
                          "The language the server browser files you under. The list is a suggestion — nothing in this tool holds a list of every locale FiveM accepts, so it takes whatever you type.",
                      },
                    ]}
                  />
                </section>

                {/* ── The folder and the paste box, paired ──
                    These two are the two halves of one task: read what is on
                    disk, then patch the config that already names it. They
                    were adjacent before the column split and the split put
                    four sections between them, so in one column a user who
                    dropped a folder scrolled past fifteen controls to reach
                    the box they were about to paste into. Adjacent in the DOM
                    fixes that at every width. They are stacked at every width
                    now: the `lg:grid-cols-2` pairing was the 1024–1279px
                    band's compensation for having no rail, and that band has
                    the rail.

                    `grid-cols-1` is load-bearing, not a default spelled out.
                    Without a template the grid's single implicit track is
                    `auto`, which grows to the widest unbreakable line inside
                    either section — a long resource name in the ordered list —
                    so at 390px and 768px both sections were ~780px wide and
                    clipped at the viewport edge, "Re-read the form" button
                    included, and the list's `overflow-x-auto` never engaged.
                    `grid-cols-1` is `minmax(0, 1fr)`, which lets it. */}
                <div className="grid grid-cols-1 gap-5">
                  {/* ── Folder ── */}
                  <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5">
                    <SectionHeading>Your resources folder</SectionHeading>

                    <div
                      data-testid="cfg-dropzone"
                      data-walkthrough="cfg-folder"
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={(event) => {
                        event.preventDefault();
                        setDragging(false);
                        onDrop(event.dataTransfer as unknown as DataTransferLike);
                      }}
                      className={`rounded-xl border border-dashed p-6 text-center transition-colors ${
                        dragging ? "border-primary/60 bg-primary/5" : "border-border"
                      }`}
                    >
                      {folderBusy ? (
                        <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Reading the folder…
                        </p>
                      ) : (
                        <>
                          {/* Prose that teaches an empty control collapses once
                              the control is used: with a folder loaded the panel
                              below already says how many resources were read and
                              whether the order is real, which is the same fact
                              stated about something rather than in advance of it. */}
                          {folder === null ? (
                            <p className="mb-3 text-xs leading-6 text-muted-foreground">
                              Drop <code className="font-mono text-foreground">resources/</code> here
                              — or a zip of it — and the{" "}
                              <code className="font-mono text-foreground">ensure</code> block is
                              written in dependency order instead of folder order. Cycles and missing
                              dependencies are named in the findings rather than quietly dropped.
                            </p>
                          ) : (
                            <p className="mb-3 text-xs leading-6 text-muted-foreground">
                              Drop another folder or zip to replace this one.
                            </p>
                          )}
                          <FolderOpen className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Drag your resources folder here, or
                          </p>
                          <div className="mt-3 flex flex-wrap justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => folderInput.current?.click()}
                              className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                            >
                              Choose a folder
                            </button>
                            <button
                              type="button"
                              onClick={() => archiveInput.current?.click()}
                              className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                            >
                              Choose zip files
                            </button>
                          </div>

                          {/* ── The worked examples, in the empty state only ──
                              Inside the zone, beside the two real pickers,
                              because the zone is the whole intake object: the
                              gesture line, the buttons and the sample are one
                              thing to decide about.

                              Rendered only while no folder is loaded. A demo may
                              never replace input somebody loaded, and the version
                              of that rule with no hidden state is that the button
                              is not there. The paste sample also waits for an
                              empty import box, so it can never overwrite a config
                              a user pasted. */}
                          {folder === null && (
                            <div className="mt-4 border-t border-border/60 pt-3 text-left">
                              <p className="text-xs font-medium text-foreground">
                                No folder to hand? Load a sample.
                              </p>
                              <div className="mt-2 flex flex-col gap-2">
                                {demoPacks.map(({ pack, use }) => (
                                  <button
                                    key={pack.id}
                                    type="button"
                                    data-testid="cfg-demo"
                                    onClick={() => void loadDemoPack(pack, use)}
                                    className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40"
                                  >
                                    <span className="block">{pack.label}</span>
                                    <span className="mt-0.5 block font-normal leading-5 text-muted-foreground">
                                      {use.teaches}
                                    </span>
                                  </button>
                                ))}
                                {existingText === "" && (
                                  <button
                                    type="button"
                                    data-testid="cfg-demo-paste"
                                    onClick={() => void loadDemoPaste()}
                                    className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40"
                                  >
                                    <span className="block">{DEMO_CFG_PASTE.label}</span>
                                    <span className="mt-0.5 block font-normal leading-5 text-muted-foreground">
                                      {DEMO_CFG_PASTE.teaches}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      <input
                        ref={folderInput}
                        data-testid="cfg-folder-input"
                        type="file"
                        multiple
                        hidden
                        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                        onChange={(event) => {
                          const files = event.target.files;
                          if (files && files.length > 0) void runIntake(() => fromDirectoryPick(files));
                        }}
                      />
                      <input
                        ref={archiveInput}
                        data-testid="cfg-archive-input"
                        type="file"
                        multiple
                        accept=".zip"
                        hidden
                        onChange={(event) => {
                          const files = Array.from(event.target.files ?? []);
                          if (files.length > 0) void runIntake(() => fromArchives(files));
                        }}
                      />
                    </div>

                    {folderError && (
                      <p className="flex items-start gap-2 text-xs leading-6 text-destructive">
                        <AlertTriangle className="mt-1 h-3.5 w-3.5 shrink-0" />
                        {folderError}
                      </p>
                    )}

                    {folder && ordered && (
                      <div className="space-y-3">
                        <p className="text-xs leading-6 text-foreground">
                          {folder.resources.length} resource
                          {folder.resources.length === 1 ? "" : "s"} read from {folder.origins} drop
                          {folder.origins === 1 ? "" : "s"}
                          {folder.categories.length > 0
                            ? `, in ${folder.categories.length} bracket folder${folder.categories.length === 1 ? "" : "s"} (${folder.categories.join(", ")})`
                            : ""}
                          .{" "}
                          {ordered.source === "dependency-graph"
                            ? "The ensure block is in dependency order."
                            : "These are in the order they were given and have NOT been ordered."}
                        </p>
                        {/* Both lists are rendered here as well as under the findings,
                            and that is deliberate rather than duplication: they are about
                            the list immediately below them. A note saying that two of the
                            folders share a name belongs next to the list that shows one of
                            them, not in a rail on the other side of the screen. */}
                        {[...ordered.notes, ...folder.truncated].length > 0 && (
                          <ul className="space-y-1 text-xs leading-6 text-warning">
                            {[...ordered.notes, ...folder.truncated].map((note) => (
                              <li key={note}>{note}</li>
                            ))}
                          </ul>
                        )}
                        {/* `overflow-x-auto` as well as `-y`: this is a `font-mono`
                            list of paths inside a bracket category, and on a phone
                            a long one scrolled the whole page sideways. */}
                        <div
                          data-walkthrough="cfg-order"
                          className="max-h-64 overflow-x-auto overflow-y-auto rounded-xl border border-border/60 bg-background p-3"
                        >
                          <ol
                            data-testid="cfg-order"
                            className="space-y-0.5 font-mono text-xs text-foreground"
                          >
                            {ordered.resources.map((resource, index) => (
                              <li key={`${resource.category ?? ""}/${resource.name}/${index}`}>
                                <span className="mr-2 inline-block w-8 text-right text-muted-foreground">
                                  {index + 1}
                                </span>
                                {resource.name}
                                {resource.category && (
                                  <span className="ml-2 text-muted-foreground">
                                    {resource.category}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    )}
                  </section>

                  {/* ── Import ── */}
                  <section
                    data-walkthrough="cfg-import"
                    className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-5"
                  >
                    <SectionHeading>Already have a server.cfg?</SectionHeading>
                    <textarea
                      id="cfg-existing"
                      data-testid="cfg-existing"
                      rows={6}
                      spellCheck={false}
                      placeholder="# paste your current server.cfg"
                      value={existingText}
                      onChange={(event) => setExistingText(event.target.value)}
                      className={INPUT_CLASS}
                    />
                    {/* Open while the box is empty, because that is when it is
                        teaching an empty control; closed once a config is pasted,
                        because from then on the merge summary and the seeded-field
                        list in the rail are saying the same things about a real
                        file rather than in advance of one. Neither of those is in
                        here. */}
                    <details
                      className="group rounded-xl border border-border/60 bg-muted/30 px-4 py-3"
                      open={existingText === ""}
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-medium text-foreground [&::-webkit-details-marker]:hidden">
                        <span>What pasting a config changes</span>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
                      </summary>
                      <p className="mt-3 text-xs leading-6 text-muted-foreground">
                        The output becomes a patch of your file rather than a new one. Comments,{" "}
                        <code className="font-mono text-foreground">exec</code> lines and every convar
                        this tool has no opinion about come back untouched. The boxes are filled in
                        from whatever your config already sets, so a setting you do not change is
                        written back as it stands.
                      </p>
                    </details>
                    {seed && (
                      <div
                        data-testid="cfg-seeded"
                        className="space-y-2 rounded-xl border border-border/60 bg-background p-3"
                      >
                        <p className="text-xs font-medium text-foreground">
                          {seed.fields.length} setting{seed.fields.length === 1 ? " was" : "s were"}{" "}
                          read out of the config you pasted and put into the form, so this patch
                          writes your own values back rather than ours.
                        </p>
                        {/* Wrap anywhere: a pasted connection string is one unbroken token and
                            at 390px it was clipped mid-host rather than read. */}
                        <ul className="space-y-1 font-mono text-xs leading-6 text-muted-foreground [overflow-wrap:anywhere]">
                          {seed.fields.map((field) => (
                            <li key={field}>{field}</li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={() => {
                            setForm(seed.previous);
                            setSeed(null);
                          }}
                          className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                        >
                          Undo — put the form back as it was
                        </button>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => cfgInput.current?.click()}
                        className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                      >
                        Load a .cfg file
                      </button>
                      {existing && (
                        <button
                          type="button"
                          onClick={() => seedFromCfg(existing)}
                          className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                        >
                          Re-read the form from this config
                        </button>
                      )}
                      {existingText !== "" && (
                        <button
                          type="button"
                          onClick={() => setExistingText("")}
                          className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                        >
                          Clear it and write a fresh file
                        </button>
                      )}
                      <input
                        ref={cfgInput}
                        data-testid="cfg-file-input"
                        type="file"
                        accept=".cfg,.txt"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) void file.text().then(setExistingText);
                        }}
                      />
                    </div>
                  </section>
                </div>
              </div>

              <div className="min-w-0 space-y-5">
                {/* ── Slots, port and state awareness ── */}
                <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5">
                  <SectionHeading>Slots, port and state awareness</SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                    <Field
                      id="cfg-port"
                      label="Port"
                      hint="Written as endpoint_add_tcp and endpoint_add_udp."
                      problem={numberProblem(form.port, 30120)}
                    >
                      <input
                        id="cfg-port"
                        className={INPUT_CLASS}
                        value={form.port}
                        inputMode="numeric"
                        onChange={(event) => update("port", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-max-clients"
                      label="sv_maxclients"
                      hint="Slots. 1–2048."
                      problem={numberProblem(form.maxClients, 48)}
                    >
                      <SuggestInput
                        id="cfg-max-clients"
                        value={form.maxClients}
                        suggestions={MAX_CLIENT_TIERS}
                        chips={MAX_CLIENT_TIERS}
                        onChip={(tier) => update("maxClients", tier)}
                        inputMode="numeric"
                        onChange={(value) => update("maxClients", value)}
                      />
                    </Field>
                    <Field id="cfg-onesync" label="onesync" hint="What state awareness runs.">
                      <select
                        id="cfg-onesync"
                        className={SELECT_CLASS}
                        value={form.oneSync}
                        onChange={(event) =>
                          update("oneSync", event.target.value as OneSyncChoice)
                        }
                      >
                        <option value="on">On — full state awareness, what most servers want</option>
                        <option value="legacy">
                          Legacy — only for resources written before OneSync went stable
                        </option>
                        <option value="off">
                          Off — no state awareness, and a hard 32-slot cap whatever sv_maxclients
                          says
                        </option>
                        <option value="leave">Leave this line out</option>
                      </select>
                    </Field>
                    <Field
                      id="cfg-build"
                      label="sv_enforceGameBuild"
                      hint="Pins the game build."
                      problem={buildProblem}
                    >
                      <div className="min-w-0 space-y-1.5">
                        <select
                          id="cfg-build"
                          className={SELECT_CLASS}
                          /* `buildValue`, not `form.enforceGameBuild`. The
                             synthetic option below is built from the trimmed
                             value and `configFromForm` writes the trimmed
                             value, so pointing the select at the raw one
                             re-opened the exact defect this control was fixed
                             for: a stored `"4012 "` matched no `<option>`, the
                             DOM fell back to the first one, and the box read
                             "Leave it to the server (no line written)" over a
                             file writing `sv_enforceGameBuild 4012`. One value
                             for the option, the select and the output. */
                          value={otherBuild ? OTHER_BUILD : buildValue}
                          onChange={(event) => {
                            const value = event.target.value;
                            // Entering the mode must not change the file, so the
                            // value is left exactly as it stands and the text box
                            // below shows it.
                            if (value === OTHER_BUILD) {
                              setOtherBuild(true);
                              return;
                            }
                            setOtherBuild(false);
                            update("enforceGameBuild", value);
                          }}
                        >
                          <option value="">Leave it to the server (no line written)</option>
                          {/* The synthetic option, and the whole of the bug fix:
                              with it the control can never render a value other
                              than the one the file writes. */}
                          {buildUnknown && !otherBuild && (
                            <option value={buildValue}>{buildValue} · not in our build table</option>
                          )}
                          {GAME_BUILDS.map((build, index) => (
                            <option key={build.build} value={build.build}>
                              {gameBuildLabel(build.build)}
                              {index === 0 ? " · latest" : ""}
                            </option>
                          ))}
                          <option value={OTHER_BUILD}>Other build…</option>
                        </select>
                        {otherBuild && (
                          <input
                            id="cfg-build-other"
                            aria-label="Game build number"
                            className={INPUT_CLASS}
                            value={form.enforceGameBuild}
                            inputMode="numeric"
                            placeholder="e.g. 4012"
                            onChange={(event) =>
                              update("enforceGameBuild", event.target.value)
                            }
                          />
                        )}
                      </div>
                    </Field>
                    <TriStateControl
                      id="cfg-onesync-population"
                      label="set onesync_population"
                      hint="Server-side population."
                      onLabel="On"
                      offLabel="Off"
                      value={form.oneSyncPopulation}
                      onChange={(value) => update("oneSyncPopulation", value)}
                    />
                  </div>
                  <WhatTheseDo
                    entries={[
                      {
                        term: "Port",
                        detail:
                          "30120 unless your host told you otherwise. One box, because the two directives are always the same port.",
                      },
                      {
                        term: "sv_maxclients",
                        detail:
                          "The artifacts refuse anything above 2048, and OneSync off caps the server at 32 whatever this says. Type what you want; anything the file could not use is listed in the rail.",
                      },
                      {
                        term: "onesync",
                        detail:
                          "Off caps the server at 32 players whatever sv_maxclients says, and nothing in the server log mentions it.",
                      },
                      {
                        term: "sv_enforceGameBuild",
                        detail:
                          "A resource streaming DLC content newer than the pinned build loads nothing, and no error is printed. The build table here is hand-kept, so a build newer than it can be typed in with “Other build…”.",
                      },
                      {
                        term: "set onesync_population",
                        detail:
                          "Server-side population costs CPU on a busy server. Leave it off unless you know you want it.",
                      },
                    ]}
                  />
                </section>

                {/* ── Keys ── */}
                <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5">
                  <SectionHeading>Keys</SectionHeading>
                  {/* One statement for all four boxes rather than the same note
                      four times. It stays visible because it is a line this tool
                      writes into the file that the user did not type. */}
                  <p className="text-xs leading-5 text-muted-foreground">
                    A key left blank is written as a commented-out line, not left out.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                    <Field
                      id="cfg-license"
                      label="sv_licenseKey"
                      hint="From keymaster.fivem.net."
                    >
                      <input
                        id="cfg-license"
                        className={INPUT_CLASS}
                        value={form.licenseKey}
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(event) => update("licenseKey", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-steam"
                      label="steam_webApiKey"
                      hint="Only needed if something identifies players by Steam."
                    >
                      <input
                        id="cfg-steam"
                        className={INPUT_CLASS}
                        value={form.steamWebApiKey}
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(event) => update("steamWebApiKey", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-rcon"
                      label="rcon_password"
                      hint="An rcon password on a public port loses servers."
                    >
                      <input
                        id="cfg-rcon"
                        className={INPUT_CLASS}
                        value={form.rconPassword}
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(event) => update("rconPassword", event.target.value)}
                      />
                    </Field>
                    <Field
                      id="cfg-mysql"
                      label="mysql_connection_string"
                      hint="Needed by ESX, QBCore and anything using a database."
                    >
                      <input
                        id="cfg-mysql"
                        className={INPUT_CLASS}
                        value={form.mysqlConnectionString}
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(event) => update("mysqlConnectionString", event.target.value)}
                      />
                    </Field>
                  </div>
                  <WhatTheseDo
                    entries={[
                      {
                        term: "sv_licenseKey",
                        detail:
                          "Issued per server at keymaster.fivem.net. The commented-out line keeps the shape of the directive in the file so it is obvious what is missing.",
                      },
                      {
                        term: "rcon_password",
                        detail:
                          "Remote console. An rcon password reachable from the internet is a way to lose a server, so leave it blank unless you know you need it.",
                      },
                      {
                        term: "mysql_connection_string",
                        detail:
                          "Every framework that stores characters, jobs or inventory needs one, and throws at startup without it.",
                      },
                    ]}
                  />
                </section>

                {/* ── Hardening ── */}
                <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5">
                  <SectionHeading>Hardening</SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                    <TriStateControl
                      id="cfg-scripthook"
                      label="sv_scriptHookAllowed"
                      hint="Single-player trainers on your server."
                      onLabel="Allow"
                      offLabel="Block"
                      value={form.scriptHookAllowed}
                      onChange={(value) => update("scriptHookAllowed", value)}
                    />
                    <TriStateControl
                      id="cfg-endpoint-privacy"
                      label="sv_endpointPrivacy"
                      hint="Hides player IP addresses."
                      onLabel="Hide IPs"
                      offLabel="Show IPs"
                      value={form.endpointPrivacy}
                      onChange={(value) => update("endpointPrivacy", value)}
                    />
                    <Toggle
                      id="cfg-server-provided"
                      label="Start the resources the artifacts ship"
                      hint="mapmanager, chat, spawnmanager, sessionmanager, basic-gamemode, hardcap."
                      note={serverProvidedNote}
                      disabled={existing !== null}
                      checked={form.startServerProvided}
                      onChange={(value) => update("startServerProvided", value)}
                    />
                  </div>
                  <WhatTheseDo
                    entries={[
                      {
                        term: "sv_scriptHookAllowed",
                        detail:
                          "Allowing it lets single-player trainers run on your server, which is how most client-side cheating arrives. Blocking it is the right answer for almost everyone.",
                      },
                      {
                        term: "sv_endpointPrivacy",
                        detail:
                          "Hides player IP addresses from other resources and from the server console.",
                      },
                      {
                        term: "Leave this line out",
                        detail:
                          "Writes no directive at all, so a config you already run keeps whatever it says — or says nothing — about that setting.",
                      },
                      {
                        term: "Server-provided resources",
                        detail:
                          "Without them there is no chat and no spawn manager. The names come from the artifacts, not from a list kept here.",
                      },
                    ]}
                  />
                </section>
              </div>

              {/* ── Lists ──
                  Full width under both columns, because three paste targets
                  three-up is a better use of the row than three narrow boxes
                  stacked in one column. */}
              <section className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-5 xl:col-span-2">
                <SectionHeading>Lists</SectionHeading>
                {/* `xl`, not `lg`: with the rail at `lg` this row is ~510px
                    wide at 1024px, and three paste targets in that are ~160px
                    textareas. Two-up until the inputs region is ~800px. */}
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  <Field
                    id="cfg-admins"
                    label="Admin identifiers"
                    hint="One per line, without the identifier. prefix."
                    note="Identifiers are not checked against anything — they are strings written into a file."
                  >
                    <textarea
                      id="cfg-admins"
                      rows={3}
                      className={INPUT_CLASS}
                      value={form.admins}
                      spellCheck={false}
                      onChange={(event) => update("admins", event.target.value)}
                    />
                  </Field>
                  <Field
                    id="cfg-execs"
                    label="exec lines"
                    hint="One path per line."
                    /* No direction in this sentence on purpose. It was written
                       as "the order below" in the same pass that moved the
                       ordered list into column A and the generated file into
                       the rail, so at `xl` nothing it could mean is below the
                       exec box: the list is up and to the left, the file is to
                       the right. Naming the thing instead of pointing at it is
                       the only version that is true at every width. */
                    note="A file you exec is not opened here, so whatever it starts is not in the ensure order and is not checked."
                  >
                    <textarea
                      id="cfg-execs"
                      rows={3}
                      className={INPUT_CLASS}
                      value={form.execs}
                      spellCheck={false}
                      onChange={(event) => update("execs", event.target.value)}
                    />
                  </Field>
                  <Field
                    id="cfg-extra-sets"
                    label="Your own convars"
                    hint="One per line: name value."
                  >
                    <textarea
                      id="cfg-extra-sets"
                      rows={3}
                      className={INPUT_CLASS}
                      value={form.extraSets}
                      spellCheck={false}
                      onChange={(event) => update("extraSets", event.target.value)}
                    />
                  </Field>
                </div>
                <WhatTheseDo
                  entries={[
                    {
                      term: "Admin identifiers",
                      detail:
                        "license:abc123…, fivem:1, steam:110000100000000. They go into group.admin, one add_principal line each.",
                    },
                    {
                      term: "exec lines",
                      detail:
                        "Paths to further config files the server should read, relative to the server data directory.",
                    },
                    {
                      term: "Your own convars",
                      detail:
                        "set stays on the server, setr reaches clients and sets reaches the server browser. A line with no keyword is written as set, because defaulting to either of the other two would quietly publish something.",
                    },
                  ]}
                />
              </section>

              {/* ── The saved form ── */}
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-5 xl:col-span-2">
                <p className="min-w-0 flex-1 text-xs leading-6 text-muted-foreground">
                  {storageRefused
                    ? "This browser refused to save the form, so a refresh will lose what you typed. A private window does this."
                    : "This form is saved in this browser so a refresh does not lose it — including the licence key. Clear it if this is not your machine."}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearStoredForm();
                    setForm(DEFAULT_SERVER_CFG_FORM);
                    setPresetNote(null);
                    setOtherBuild(false);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-destructive/40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Forget saved settings
                </button>
              </div>
            </div>
          </div>

          {/* ── The output rail ──
              Adjustments first, then the file, then the findings: what we
              changed about your input, then what we wrote, then what is wrong
              with it and what was not checked. Cause, effect, judgement — and
              the smallest panel at the top, so a four-hundred-line `<pre>` can
              never push a clamp notice out of view. It scrolls in itself rather
              than lengthening the page. */}
          <div className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:col-span-5 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto xl:col-span-4 2xl:col-span-5">
            {adjustments.length > 0 && (
              <div className="space-y-2 rounded-2xl border border-warning/40 bg-warning/5 p-4">
                <p className="text-xs font-medium text-foreground">Changed while writing this file</p>
                <ul
                  data-testid="cfg-adjustments"
                  className="space-y-2 text-xs leading-6 text-muted-foreground"
                >
                  {adjustments.map((adjustment, index) => (
                    <li key={`${adjustment.field}-${index}`}>
                      <span className="font-mono text-foreground">{adjustment.field}</span>: you gave{" "}
                      <span className="font-mono text-foreground">{adjustment.given}</span>, this
                      file uses <span className="font-mono text-foreground">{adjustment.used}</span>.{" "}
                      {adjustment.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Output ── */}
            <section
              data-walkthrough="cfg-output"
              className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-5"
            >
              {/* The badge sits above the file rather than beside the count,
                  because on this tool the file IS the result. Visible, never
                  behind a disclosure, and it says what to do about it. */}
              {fromDemo && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2">
                  <span
                    data-testid="cfg-demo-state"
                    className="inline-flex items-center gap-2 text-xs font-medium text-foreground"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                    {DEMO_STATE_LABEL}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <FileCog className="h-4 w-4 text-muted-foreground" />
                <SectionHeading>
                  {output.mode === "merged" ? "Your server.cfg, patched" : "server.cfg"}
                </SectionHeading>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    /* The clipboard was the one unmarked channel in the feature.
                       Every other demo-derived artifact says what it is: the
                       download is renamed, `DEMO_INPUT_NOTE` rides `skipped`
                       into the findings panel and the exports, and the badge is
                       on screen. The copy is silent by construction — the text
                       is deliberately left byte for byte, because in merge mode
                       "your config comes back unchanged" is the feature — and
                       on this tool the copied text *is* the deliverable, so
                       clipboard → live server → restart is the path a user
                       takes. What could not go in the artifact goes on the
                       gesture: the button says what it is about to copy, and
                       what it copied. */
                    data-testid="cfg-copy"
                    onClick={() => {
                      try {
                        void navigator.clipboard?.writeText(output.text);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1200);
                      } catch {
                        /* No clipboard: the text is on screen and selectable. */
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-success" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {fromDemo
                      ? copied
                        ? "Copied — sample pack"
                        : "Copy the sample"
                      : copied
                        ? "Copied"
                        : "Copy"}
                  </button>
                  <button
                    type="button"
                    /* Renamed while a sample is loaded, and the file itself is
                       left alone on purpose: a `#` line we inject would be a
                       line this tool added to a config it promises comes back
                       byte for byte, and in merge mode that promise is the
                       feature. The name carries the fact to the downloads
                       folder; the badge above and the findings panel below
                       carry it on screen. */
                    onClick={() =>
                      downloadTextFile(
                        fromDemo ? demoDownloadName("server.cfg") : "server.cfg",
                        output.text,
                        "text/plain",
                      )
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </button>
                </div>
              </div>

              {output.mode === "merged" && (
                <div className="space-y-2">
                  <p className="text-xs leading-6 text-muted-foreground">
                    {output.changes.length === 0
                      ? "Nothing in the config you pasted had to change. The file below is byte for byte what you gave us."
                      : `${output.changes.length} line${output.changes.length === 1 ? "" : "s"} of the config you pasted ` +
                        "are different below, and every one of them is listed here. Everything else — " +
                        "your comments, your exec lines and every convar this tool has no opinion " +
                        "about — is byte for byte what you gave us."}
                  </p>
                  {output.changes.length > 0 && (
                    /* `overflow-x-auto`: these are whole `server.cfg` lines, and
                       a `mysql_connection_string` is longer than a phone.

                       `max-h-80 overflow-y-auto`: a pasted config with a real
                       folder rewrites its whole ensure block, and with 292
                       resources that was 298 lines here — ~8,000px, which put
                       the `<pre>` a long way down inside the sticky rail's own
                       scroll and made the page 25,000px tall at 390px. Every
                       line is still rendered and reachable, in a box that
                       scrolls, exactly as the ordered list does; this is not a
                       disclosure and nothing in it is hidden behind a toggle. */
                    <ul
                      data-testid="cfg-changes"
                      className="max-h-80 space-y-1 overflow-x-auto overflow-y-auto rounded-xl border border-border/60 bg-background p-3 font-mono text-xs leading-6 text-muted-foreground"
                    >
                      {output.changes.map((change, index) => (
                        <li key={`${change.after}-${index}`}>
                          {change.before === null ? (
                            <>
                              <span className="text-muted-foreground">added</span>{" "}
                              <span className="text-foreground">{change.after}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-foreground">{change.before}</span>
                              <span className="text-muted-foreground"> → </span>
                              <span className="text-foreground">{change.after}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {output.refused.length > 0 && (
                <ul className="space-y-1 text-xs leading-6 text-warning">
                  {output.refused.map((reason, index) => (
                    <li key={index}>Refused: {reason}</li>
                  ))}
                </ul>
              )}

              <pre
                data-testid="cfg-output"
                className="max-h-[32rem] overflow-auto rounded-xl border border-border/60 bg-background px-4 py-3 text-xs leading-6 text-foreground"
              >
                {output.text}
              </pre>
            </section>

            {/* ── Findings ── */}
            <section
              data-walkthrough="cfg-findings"
              className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-5"
            >
              <SectionHeading>What is wrong with it</SectionHeading>
              <FindingsList
                report={report}
                /* The demo sentence joins the same list intake's refusals ride
                   in, which is where this layer keeps everything that was not
                   done. One line puts it on screen; the file's own name carries
                   it off the page. */
                truncated={[
                  ...(folder?.truncated ?? []),
                  ...(fromDemo ? [DEMO_INPUT_NOTE] : []),
                ]}
                emptyLabel="Nothing wrong in the checks that ran. Read what was not checked, below."
              />
            </section>
          </div>
        </div>
      </div>
      </FreeToolGate>

      {children}

      {/* Manual mode: a demo button raises the signal and nothing else does.
          This tool's primary mode is an empty form that no folder is ever
          dropped on, and a tour that opened by itself would land on a user who
          is halfway through typing a hostname. */}
      <ToolWalkthrough
        /* The sample-announcing first step is dropped when no sample is
           loaded, and this is an honesty fix rather than a tidy-up. That step
           says "not your files" — true over a demo, and a straightforward
           falsehood over a folder the reader picked themselves, which the
           permanent "Show me how this works" control now makes reachable.
           Filtered by `id` and not by index so reordering the array cannot
           silently drop a different step. Nothing is lost on the demo path,
           and nothing is claimed on the real one. */
        steps={fromDemo ? serverCfgSteps : serverCfgSteps.filter((step) => step.id !== "welcome")}
        storageKey="workshop_tour_server_cfg_v1"
        finishLabel={serverCfgFinishLabel}
        openSignal={tourSignal}
      />
    </FreeToolShell>
  );
}

export default ServerCfgClient;
